// src/utils/api.ts
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios'

// ============================================
// TYPES & INTERFACES
// ============================================

export interface User {
  id: string
  name: string
  email: string
  phone?: string
  company?: string
  jobTitle?: string
  industry?: string
  companySize?: string
  emailVerified: boolean
  avatar?: string
  role: 'user' | 'admin' | 'premium'
  plan: 'starter' | 'pro' | 'enterprise'
  createdAt: string
  updatedAt?: string
}

export interface SignUpData {
  name: string
  email: string
  password: string
  confirmPassword: string
  phone: string
  company: string
  jobTitle: string
  industry: string
  companySize: string
}

export interface SignInData {
  email: string
  password: string
  rememberMe?: boolean
}

export interface VerifyOTPData {
  email: string
  otp: string
  purpose?: string
}

export interface ForgotPasswordData {
  email: string
}

export interface ResetPasswordData {
  email: string
  otp: string
  newPassword: string
  confirmPassword: string
}

export interface Campaign {
  id: string
  name: string
  subject: string
  content: string
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed'
  type: 'regular' | 'automated' | 'ab-test'
  template?: string
  recipients?: number
  opens?: number
  clicks?: number
  bounces?: number
  openRate?: number
  clickRate?: number
  scheduledFor?: string
  sentAt?: string
  createdAt: string
  updatedAt: string
  tags?: string[]
  abTest?: {
    variants: string[]
    winner?: string
    completedAt?: string
  }
  // ✨ NEW: Share fields
  shareToken?: string
  shareEnabled?: boolean
  shareCreatedAt?: Date
  viewCount?: number
  subscriberCount?: number
  shareAnalytics?: {
    totalViews: number
    totalClicks: number
    conversionRate: number
    lastViewedAt?: Date
  }
}

export interface CreateCampaignData {
  name: string
  subject: string
  content: string
  type?: 'regular' | 'automated' | 'ab-test'
  template?: string
  scheduledFor?: string
  tags?: string[]
}

export interface CampaignStats {
  total: number
  sent: number
  drafts: number
  scheduled: number
  openRate: number
  clickRate: number
  bounceRate: number
}

export interface Template {
  id: string
  name: string
  description: string
  category: string
  content: string
  thumbnail?: string
  isPremium: boolean
  popular?: boolean
  variables?: string[]
  createdAt: string
  updatedAt: string
}

export interface Subscriber {
  _id?: string
  id?: string
  email: string
  name?: string
  phone?: string
  status?: 'active' | 'unsubscribed' | 'bounced'
  subscribedAt?: string
  lastOpened?: string
  tags?: string[]
  ipAddress?: string
  userAgent?: string
  source?: 'share_link' | 'direct' | 'import' | 'api'
  clicks?: number
  isActive?: boolean
}

export interface SubscriberStats {
  total: number
  active: number
  unsubscribed: number
  bounced: number
  growthRate: number
}

export interface DashboardStats {
  campaigns: CampaignStats
  subscribers: SubscriberStats
  emailsSent: number
  avgOpenRate: number
  avgClickRate: number
  aiUsage: {
    used: number
    limit: number
    percentage: number
  }
  subscriberUsage: {
    used: number
    limit: number
    percentage: number
  }
  recentCampaigns: Campaign[]
}

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  message?: string
  error?: string
  errors?: Record<string, string[]>
  meta?: {
    page?: number
    limit?: number
    total?: number
    totalPages?: number
  }
}

export interface AuthResponse {
  user: User
  token: string
  refreshToken: string
  expiresIn: number
}

export interface EmailCheckResponse {
  available: boolean
  exists?: boolean
  provider?: string
  authType?: 'password' | 'oauth'
}

// ============================================
// ERROR CLASSES
// ============================================

export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public errors?: Record<string, string[]>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export class NetworkError extends Error {
  constructor() {
    super('Network error. Please check your internet connection.')
    this.name = 'NetworkError'
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message: string = 'Session expired. Please login again.') {
    super(401, message)
    this.name = 'UnauthorizedError'
  }
}

export class ForbiddenError extends ApiError {
  constructor(message: string = 'You do not have permission to perform this action') {
    super(403, message)
    this.name = 'ForbiddenError'
  }
}

export class NotFoundError extends ApiError {
  constructor(message: string = 'Resource not found') {
    super(404, message)
    this.name = 'NotFoundError'
  }
}

export class ValidationError extends ApiError {
  constructor(message: string = 'Validation failed', errors?: Record<string, string[]>) {
    super(422, message, errors)
    this.name = 'ValidationError'
  }
}

export class VerificationRequiredError extends ApiError {
  constructor(message: string = 'Email verification required') {
    super(403, message)
    this.name = 'VerificationRequiredError'
  }
}

export class RateLimitError extends ApiError {
  constructor(message: string = 'Too many requests. Please try again later.') {
    super(429, message)
    this.name = 'RateLimitError'
  }
}

// ============================================
// API CONFIGURATION
// ============================================

// Get the API key from the environment
const API_KEY = import.meta.env.VITE_API_KEY;

const API_CONFIG = {
  baseURL: import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'x-api-key': API_KEY, // ✅ ADD THIS LINE
  },
}

console.log('🔗 API initialized with baseURL:', API_CONFIG.baseURL)


// ============================================
// API SERVICE CLASS
// ============================================

class ApiService {
  private api: AxiosInstance
  private refreshSubscribers: ((token: string) => void)[] = []
  private isRefreshing = false

  constructor() {
    this.api = axios.create(API_CONFIG)
    this.setupInterceptors()
    console.log('✅ ApiService initialized successfully')
  }

  // ============================================
  // INTERCEPTORS
  // ============================================

  private setupInterceptors(): void {
    this.api.interceptors.request.use(
      this.handleRequest.bind(this),
      this.handleRequestError.bind(this)
    )

    this.api.interceptors.response.use(
      this.handleResponse.bind(this),
      this.handleResponseError.bind(this)
    )
  }

  private handleRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    // ✅ FIXED: Don't send token for public auth endpoints
    const publicAuthEndpoints = [
      '/auth/signin',
      '/auth/signup',
      '/auth/send-otp',
      '/auth/verify-otp',
      '/auth/check-email',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/refresh',
      '/auth/select-plan',
      '/auth/payments/create-paypal-order',
      '/auth/payments/capture-paypal-order',
      '/auth/payments/check-status'
    ]

    const isPublicAuth = publicAuthEndpoints.some(endpoint => config.url?.includes(endpoint))

    if (!isPublicAuth) {
      const token = this.getAccessToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    config.headers['X-Request-ID'] = this.generateRequestId()

    if (config.method?.toLowerCase() === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now()
      }
    }

    console.log(`📤 ${config.method?.toUpperCase()} ${config.url}`)
    return config
  }

  private handleRequestError(error: AxiosError): Promise<AxiosError> {
    console.error('❌ Request error:', error)
    return Promise.reject(error)
  }

  private handleResponse(response: AxiosResponse): AxiosResponse {
    console.log(`📥 ${response.status} ${response.config.url}`)
    return response
  }

  private async handleResponseError(error: AxiosError): Promise<any> {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (!error.response) {
      console.error('❌ Network error')
      return Promise.reject(new NetworkError())
    }

    const { status, data } = error.response as AxiosResponse

    console.error(`❌ ${status} ${originalRequest.url}`, data)

    if (status === 401 && !originalRequest._retry) {
  // Skip token refresh on public pages
  if (window.location.pathname === '/' || window.location.pathname === '/signin' || window.location.pathname === '/signup') {
    this.clearTokens()
    return Promise.reject(new UnauthorizedError())
  }
  return this.handleTokenRefresh(originalRequest)
}

    if (status === 403) {
      if ((data as any)?.requiresVerification) {
        return Promise.reject(new VerificationRequiredError((data as any).message))
      }
      return Promise.reject(new ForbiddenError((data as any)?.message))
    }

    if (status === 404) {
      return Promise.reject(new NotFoundError((data as any)?.message))
    }

    if (status === 422) {
      return Promise.reject(new ValidationError((data as any)?.message, (data as any)?.errors))
    }

    if (status === 429) {
      return Promise.reject(new RateLimitError((data as any)?.message))
    }

    const apiError = new ApiError(
      status,
      (data as any)?.message || 'An error occurred',
      (data as any)?.errors
    )

    return Promise.reject(apiError)
  }

  // ============================================
  // TOKEN MANAGEMENT
  // ============================================

  private async handleTokenRefresh(originalRequest: InternalAxiosRequestConfig & { _retry?: boolean }) {
    if (this.isRefreshing) {
      return new Promise(resolve => {
        this.refreshSubscribers.push((token: string) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          resolve(this.api(originalRequest))
        })
      })
    }

    originalRequest._retry = true
    this.isRefreshing = true

    try {
      const refreshToken = this.getRefreshToken()
      if (!refreshToken) {
        throw new Error('No refresh token')
      }

      const response = await this.refreshAccessToken(refreshToken)
      const { token, refreshToken: newRefreshToken } = (response.data as any).data

      this.setTokens(token, newRefreshToken)

      this.refreshSubscribers.forEach(callback => callback(token))
      this.refreshSubscribers = []

      originalRequest.headers.Authorization = `Bearer ${token}`
      return this.api(originalRequest)
    } catch (error) {
      this.clearTokens()
      this.refreshSubscribers = []
      window.location.href = '/signin?session=expired'
      return Promise.reject(new UnauthorizedError())
    } finally {
      this.isRefreshing = false
    }
  }

  private getAccessToken(): string | null {
    return localStorage.getItem('accessToken') || localStorage.getItem('token')
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken')
  }

  private setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('accessToken', accessToken)
    localStorage.setItem('token', accessToken)
    localStorage.setItem('refreshToken', refreshToken)
    const expiresAt = Date.now() + 3600000
    localStorage.setItem('tokenExpiresAt', expiresAt.toString())
  }

  public clearTokens(): void {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('tokenExpiresAt')
    localStorage.removeItem('user')
  }

  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  // ============================================
  // AUTHENTICATION METHODS
  // ============================================

  async checkEmail(email: string): Promise<EmailCheckResponse> {
    const response = await this.api.post<ApiResponse<EmailCheckResponse>>('/auth/check-email', { email })
    return response.data.data!
  }

  async sendOTP(email: string): Promise<{ message: string; expiresIn: number }> {
    const response = await this.api.post<ApiResponse<{ message: string; expiresIn: number }>>('/auth/send-otp', { email })
    return response.data.data!
  }

  async verifyOTP(data: VerifyOTPData): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>('/auth/verify-otp', data)
    // Return full response with success flag so component can check it
    console.log('📝 verifyOTP response:', {
      success: response.data.success,
      message: response.data.message,
      nextStep: response.data.data?.nextStep
    })
    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data
    }
}

  async signUp(data: SignUpData): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>('/auth/signup', data)
    const authData = response.data.data!
    this.setTokens(authData.token, authData.refreshToken)
    localStorage.setItem('user', JSON.stringify(authData.user))
    return authData
  }

  // api.ts - CORRECTED signIn function
// Find the signIn function and replace it with this:

  async signIn(data: SignInData): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>('/auth/signin', data)
    
    // ✅ FIXED: Handle both response structures
    // Backend returns: { success: true, data: { user, token, refreshToken } }
    const authData = response.data.data || response.data
    
    if (!authData || !authData.token) {
      console.error('❌ Invalid sign in response:', response.data)
      throw new Error('Invalid response from server: missing token')
    }

    console.log('✅ Token received:', authData.token.substring(0, 20) + '...')
    
    this.setTokens(authData.token, authData.refreshToken)
    localStorage.setItem('user', JSON.stringify(authData.user))

    if (data.rememberMe) {
      localStorage.setItem('rememberMe', 'true')
    }

    return authData
  }

  async signOut(): Promise<void> {
    try {
      await this.api.post('/auth/signout')
    } finally {
      this.clearTokens()
      localStorage.removeItem('user')
      localStorage.removeItem('rememberMe')
    }
  }

  async refreshAccessToken(refreshToken: string): Promise<AxiosResponse<AuthResponse>> {
    return this.api.post('/auth/refresh', { refreshToken })
  }

  async forgotPassword(data: ForgotPasswordData): Promise<{ message: string }> {
    const response = await this.api.post<ApiResponse<{ message: string }>>('/auth/forgot-password', data)
    return response.data.data!
  }

  async resetPassword(data: ResetPasswordData): Promise<{ message: string }> {
    const response = await this.api.post<ApiResponse<{ message: string }>>('/auth/reset-password', data)
    return response.data.data!
  }

  // ============================================
  // USER METHODS
  // ============================================

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>('/user/profile')
    const user = response.data.data!
    localStorage.setItem('user', JSON.stringify(user))
    return user
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await this.api.patch<ApiResponse<User>>('/user/profile', data)
    const user = response.data.data!
    localStorage.setItem('user', JSON.stringify(user))
    return user
  }

  async changePassword(oldPassword: string, newPassword: string, confirmPassword: string): Promise<{ message: string }> {
    const response = await this.api.put<ApiResponse<{ message: string }>>('/user/change-password', {
      oldPassword,
      newPassword,
      confirmPassword
    })
    return response.data.data!
  }

  async requestEmailVerification(): Promise<{ message: string }> {
    const response = await this.api.post<ApiResponse<{ message: string }>>('/user/request-email-verification')
    return response.data.data!
  }

  async verifyEmail(otp: string): Promise<{ emailVerified: boolean }> {
    const response = await this.api.post<ApiResponse<{ emailVerified: boolean }>>('/user/verify-email', { otp })
    return response.data.data!
  }

  // ============================================
  // CAMPAIGN METHODS
  // ============================================

  async getCampaigns(params?: { page?: number; limit?: number; status?: string }): Promise<Campaign[]> {
    const response = await this.api.get<ApiResponse<Campaign[]>>('/campaigns', { params })
    return response.data.data!
  }

  async getCampaign(id: string): Promise<Campaign> {
    const response = await this.api.get<ApiResponse<Campaign>>(`/campaigns/${id}`)
    return response.data.data!
  }

  async createCampaign(data: CreateCampaignData): Promise<Campaign> {
    const response = await this.api.post<ApiResponse<Campaign>>('/campaigns', data)
    return response.data.data!
  }

  async updateCampaign(id: string, data: Partial<CreateCampaignData>): Promise<Campaign> {
    const response = await this.api.patch<ApiResponse<Campaign>>(`/campaigns/${id}`, data)
    return response.data.data!
  }

  async deleteCampaign(id: string): Promise<{ message: string }> {
    const response = await this.api.delete<ApiResponse<{ message: string }>>(`/campaigns/${id}`)
    return response.data.data!
  }

  async duplicateCampaign(id: string): Promise<Campaign> {
    const response = await this.api.post<ApiResponse<Campaign>>(`/campaigns/${id}/duplicate`)
    return response.data.data!
  }

  async sendCampaign(id: string, scheduledFor?: string): Promise<{ message: string; sentAt: string }> {
    const response = await this.api.post<ApiResponse<{ message: string; sentAt: string }>>(`/campaigns/${id}/send`, {
      scheduledFor
    })
    return response.data.data!
  }

  async scheduleCampaign(id: string, scheduledFor: string): Promise<Campaign> {
    const response = await this.api.post<ApiResponse<Campaign>>(`/campaigns/${id}/schedule`, { scheduledFor })
    return response.data.data!
  }

  async cancelScheduledCampaign(id: string): Promise<Campaign> {
    const response = await this.api.post<ApiResponse<Campaign>>(`/campaigns/${id}/cancel`)
    return response.data.data!
  }

  // ============================================
  // CAMPAIGN SHARING & SUBSCRIBERS METHODS
  // ============================================

  async getCampaignByShareToken(shareToken: string): Promise<Campaign> {
    const response = await this.api.get<ApiResponse<Campaign>>(`/campaigns/share/${shareToken}`)
    return response.data.data!
  }

  async addCampaignSubscriber(
    shareToken: string,
    data: {
      name: string
      email: string
      phone?: string
      ipAddress?: string
      userAgent?: string
      subscribedAt?: string
    }
  ): Promise<Subscriber> {
    const response = await this.api.post<ApiResponse<Subscriber>>(`/campaigns/share/${shareToken}/subscribe`, data)
    return response.data.data!
  }

  async generateShareToken(campaignId: string): Promise<{ shareToken: string; shareUrl: string; shareEnabled: boolean; subscriberCount: number }> {
    const response = await this.api.post<ApiResponse<{ shareToken: string; shareUrl: string; shareEnabled: boolean; subscriberCount: number }>>(`/campaigns/${campaignId}/generate-share`)
    return response.data.data!
  }

  async getCampaignWithSubscribers(campaignId: string): Promise<{
    campaign: Partial<Campaign>
    subscribers: Subscriber[]
  }> {
    const response = await this.api.get<ApiResponse<{ campaign: Partial<Campaign>; subscribers: Subscriber[] }>>(`/campaigns/${campaignId}/subscribers`)
    return response.data.data!
  }

  async getCampaignAnalytics(campaignId: string): Promise<{
    totalViews: number
    totalSubscribers: number
    conversionRate: string
    sourceBreakdown: { shareLink: number; direct: number }
    recentSubscribers: Subscriber[]
  }> {
    const response = await this.api.get<ApiResponse<any>>(`/campaigns/${campaignId}/analytics`)
    return response.data.data!
  }

  async exportSubscribers(campaignId: string): Promise<Blob> {
    const response = await this.api.get(`/campaigns/${campaignId}/subscribers/export`, {
      responseType: 'blob'
    })
    return response.data
  }

  async deleteSubscriber(campaignId: string, subscriberId: string): Promise<{ message: string }> {
    const response = await this.api.delete<ApiResponse<{ message: string }>>(`/campaigns/${campaignId}/subscribers/${subscriberId}`)
    return response.data.data!
  }

  // ============================================
  // TEMPLATE METHODS
  // ============================================

  async getTemplates(params?: { category?: string; limit?: number }): Promise<Template[]> {
    const response = await this.api.get<ApiResponse<Template[]>>('/templates', { params })
    return response.data.data!
  }

  async getTemplate(id: string): Promise<Template> {
    const response = await this.api.get<ApiResponse<Template>>(`/templates/${id}`)
    return response.data.data!
  }

  async createTemplate(data: Partial<Template>): Promise<Template> {
    const response = await this.api.post<ApiResponse<Template>>('/templates', data)
    return response.data.data!
  }

  async updateTemplate(id: string, data: Partial<Template>): Promise<Template> {
    const response = await this.api.patch<ApiResponse<Template>>(`/templates/${id}`, data)
    return response.data.data!
  }

  async deleteTemplate(id: string): Promise<{ message: string }> {
    const response = await this.api.delete<ApiResponse<{ message: string }>>(`/templates/${id}`)
    return response.data.data!
  }

  // ============================================
  // SUBSCRIBER METHODS
  // ============================================

  async getSubscribers(params?: { page?: number; limit?: number; status?: string }): Promise<Subscriber[]> {
    const response = await this.api.get<ApiResponse<Subscriber[]>>('/subscribers', { params })
    return response.data.data!
  }

  async getSubscriber(id: string): Promise<Subscriber> {
    const response = await this.api.get<ApiResponse<Subscriber>>(`/subscribers/${id}`)
    return response.data.data!
  }

  async addSubscriber(data: { email: string; name?: string; tags?: string[] }): Promise<Subscriber> {
    const response = await this.api.post<ApiResponse<Subscriber>>('/subscribers', data)
    return response.data.data!
  }

  async importSubscribers(data: { subscribers: { email: string; name?: string }[] }): Promise<{
    imported: number
    failed: number
    errors?: string[]
  }> {
    const response = await this.api.post<ApiResponse<any>>('/subscribers/import', data)
    return response.data.data!
  }

  async updateSubscriber(id: string, data: Partial<Subscriber>): Promise<Subscriber> {
    const response = await this.api.patch<ApiResponse<Subscriber>>(`/subscribers/${id}`, data)
    return response.data.data!
  }

  async deleteSubscriberFromList(id: string): Promise<{ message: string }> {
    const response = await this.api.delete<ApiResponse<{ message: string }>>(`/subscribers/${id}`)
    return response.data.data!
  }

  // ============================================
  // ANALYTICS METHODS
  // ============================================

  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.api.get<ApiResponse<DashboardStats>>('/analytics/dashboard')
    return response.data.data!
  }

  async getSubscriberAnalytics(): Promise<SubscriberStats> {
    const response = await this.api.get<ApiResponse<SubscriberStats>>('/analytics/subscribers')
    return response.data.data!
  }

  // ============================================
  // AI METHODS
  // ============================================

  async generateEmailContent(prompt: string, tone?: string): Promise<{ content: string; subject?: string }> {
    const response = await this.api.post<ApiResponse<{ content: string; subject?: string }>>('/ai/generate', {
      prompt,
      tone
    })
    return response.data.data!
  }

  async optimizeSubjectLine(content: string): Promise<{ suggestions: string[] }> {
    const response = await this.api.post<ApiResponse<{ suggestions: string[] }>>('/ai/optimize-subject', {
      content
    })
    return response.data.data!
  }

  async analyzeCampaign(campaignId: string): Promise<{
    score: number
    suggestions: string[]
    spamScore: number
  }> {
    const response = await this.api.post<ApiResponse<any>>(`/ai/analyze/${campaignId}`)
    return response.data.data!
  }

  // ============================================
  // BILLING & USAGE METHODS
  // ============================================

  async getUsage(): Promise<{
    ai: { used: number; limit: number; percentage: number }
    subscribers: { used: number; limit: number; percentage: number }
    emails: { sent: number; limit: number }
  }> {
    const response = await this.api.get<ApiResponse<any>>('/billing/usage')
    return response.data.data!
  }

  async getPlans(): Promise<Array<{
    id: string
    name: string
    price: number
    features: string[]
    limits: {
      subscribers: number
      emails: number
      aiCredits: number
    }
  }>> {
    const response = await this.api.get<ApiResponse<any>>('/billing/plans')
    return response.data.data!
  }

  async upgradePlan(planId: string): Promise<{ message: string; newPlan: string }> {
    const response = await this.api.post<ApiResponse<{ message: string; newPlan: string }>>('/billing/upgrade', {
      planId
    })
    return response.data.data!
  }
}

// ============================================
// SINGLETON INSTANCE & EXPORTS
// ============================================

export const apiService = new ApiService()
export default apiService